import { Box, Button, IconButton, Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid/Grid";
import { StyledPaper } from "components/Styled";
import styled from "styled-components";
import { TextField } from "@material-ui/core";
import { useEffect, useState } from "react";
import CloseIcon from "@material-ui/icons/Close";
import formatBytes from "utils/formatBytes";
import apiRequest, { getErrorCode } from "utils/apiRequestWithErrorCode";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import ButtonWithLoading from "components/ButtonWithLoading";
import { useHistory } from "react-router-dom";

export default function Form() {
  const { t } = useTranslation("smime");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const [file, setFile] = useState<File>();
  const [name, setName] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [error, setError] = useState<string>();

  const [isSaving, setIsSaving] = useState(false);

  let step = file ? 1 : 0;
  let type = file?.name.split(".").pop() as "pem" | "p12";

  const haveErrorInPassword =
    error === "PASSWORD_IS_REQUIRED" || error === "WRONG_PASSWORD";

  useEffect(() => {
    if (error && !haveErrorInPassword) {
      // ALREADY_EXISTS | INVALID_CERTIFICATE
      enqueueSnackbar(t([`error.${error}`, "error.UNKNOWN_ERROR"]), {
        variant: "error",
      });
      console.error("Error saving certificate:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  async function save() {
    if (!file || !name?.trim()) return;

    setError(undefined);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    if (description) {
      formData.append("description", description);
    }
    if (password) {
      formData.append("password", password || "");
    }

    try {
      setIsSaving(true);
      await apiRequest("POST", "/smime/certs", formData);
      history.push("/smime-certs");
    } catch (error) {
      setError(getErrorCode(error));
      console.error("Error saving certificate:", error);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Grid container>
      <Grid item xs={8}>
        <StyledPaper>
          <form
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <Typography variant="h6">{t("form.createTitle")}</Typography>
            {step === 0 && (
              <>
                <Typography>{t("form.explainTitle")}</Typography>

                <input
                  id="contained-button-file"
                  type="file"
                  style={{ display: "none" }}
                  accept=".pem,.p12"
                  onChange={async event => {
                    const file = event.target.files?.[0];
                    if (file) setFile(file);
                  }}
                />
                <label htmlFor="contained-button-file">
                  <Button
                    variant="contained"
                    color="primary"
                    component="span"
                    disableElevation
                  >
                    {t("form.addCertBtn")}
                  </Button>
                </label>
              </>
            )}

            {step === 1 && (
              <>
                <Typography>{t("form.stepTitle")}</Typography>

                <FileItem
                  file={file}
                  onRemove={() => {
                    setFile(undefined);
                    setName("");
                    setDescription("");
                    setPassword("");
                    setError(undefined);
                  }}
                />

                <Box mb="10px">
                  <Typography>{t("form.stepHint")}</Typography>
                </Box>

                <StyledTextField
                  label={t("form.nameLabel")}
                  variant="outlined"
                  fullWidth
                  required
                  onChange={event => setName(event.target.value)}
                  value={name}
                />
                <StyledTextField
                  label={t("form.descriptionLabel")}
                  variant="outlined"
                  fullWidth
                  onChange={event => setDescription(event.target.value)}
                  value={description}
                  helperText={t("form.descriptionHint")}
                />

                {type === "p12" && (
                  <StyledTextField
                    label={t("form.passwordLabel")}
                    variant="outlined"
                    type="password"
                    fullWidth
                    helperText={
                      haveErrorInPassword
                        ? t(`error.${error}`)
                        : t("form.passwordHint")
                    }
                    error={haveErrorInPassword}
                    onChange={event => setPassword(event.target.value)}
                    value={password}
                  />
                )}

                <ButtonWithLoading
                  loading={isSaving}
                  variant="contained"
                  color="primary"
                  disableElevation
                  disabled={!name?.trim()}
                  onClick={save}
                >
                  {t("form.saveBtn")}
                </ButtonWithLoading>
              </>
            )}
          </form>
        </StyledPaper>
      </Grid>
    </Grid>
  );
}

function FileItem({ file, onRemove }: { file: File; onRemove: () => void }) {
  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        border="1px solid rgba(0, 0, 0, 0.12)"
        borderRadius="4px"
        p={2}
        maxWidth="350px"
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="body1">{file.name}</Typography>
          <Typography variant="caption">{formatBytes(file.size)}</Typography>
        </Box>

        <IconButton aria-label="delete" onClick={onRemove}>
          <CloseIcon />
        </IconButton>
      </Box>
    </>
  );
}

const StyledTextField = styled(TextField)({
  marginBottom: "12px",
});
